import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase, refreshSessionWithRetry, isTransientError } from '../config/supabase';
import { supabaseAuthService } from '../services/supabaseAuthService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track consecutive refresh failures to prevent false sign-outs
  // Using ref instead of state since we only need to track internally
  const consecutiveRefreshFailuresRef = useRef(0);
  const MAX_CONSECUTIVE_FAILURES = 3;

  // Fetch user profile and tenant info from database
  // This function will keep retrying until successful - profile is critical
  const fetchUserData = async (userId, retryCount = 0, isBackgroundRetry = false) => {
    const maxRetries = 2; // Reduced retries - don't block UI too long
    try {
      // if (!isBackgroundRetry) {
      //   console.log('Fetching user data for:', userId);
      // }

      // Reduced timeout to 8 seconds - balance between reliability and speed
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 8000)
      );

      // Fetch user profile - use simpler query with just essential fields first
      // This is faster than selecting all fields
      const fetchPromise = supabase
        .from('users')
        .select('id, email, first_name, last_name, tenant_id, tenant_role, platform_role, is_active, approval_status')
        .eq('id', userId)
        .maybeSingle(); // Use maybeSingle() instead of array - more efficient

      let profileData, profileError;
      try {
        const result = await Promise.race([
          fetchPromise,
          timeoutPromise
        ]);
        profileData = result.data;
        profileError = result.error;
      } catch (timeoutErr) {
        // Timeout occurred - retry quickly or schedule background retry
        if (retryCount < maxRetries && !isBackgroundRetry) {
          // Quick retry for initial load (1 second)
          if (import.meta.env.DEV) console.log(`Profile fetch timeout, quick retry (${retryCount + 1}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchUserData(userId, retryCount + 1, false);
        }
        // Max retries exceeded or background retry - schedule background retry
        if (!isBackgroundRetry) {
          if (import.meta.env.DEV) console.log('Profile fetch timeout, will retry in background');
        }
        // Schedule a background retry after 3 seconds
        setTimeout(() => {
          fetchUserData(userId, 0, true); // Reset retry count for background retry
        }, 3000);
        return;
      }

      // if (!isBackgroundRetry) {
      //   console.log('Profile fetch result:', { profileData, profileError });
      // }

      if (profileError) {
        // Profile fetch error - quick retry or schedule background retry
        if (retryCount < maxRetries && !isBackgroundRetry && !profileError.message?.includes('not found')) {
          if (import.meta.env.DEV) console.log(`Profile fetch error, quick retry (${retryCount + 1}/${maxRetries})...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchUserData(userId, retryCount + 1, false);
        }
        // If "not found" error, profile might not exist yet (during signup)
        if (profileError.message?.includes('not found')) {
          if (import.meta.env.DEV) console.log('Profile not found yet (may still be creating)');
          // Schedule background retry
          setTimeout(() => {
            fetchUserData(userId, 0, true);
          }, 3000);
          return;
        }
        // Other errors - schedule background retry
        if (!isBackgroundRetry) {
          console.warn('Profile fetch error, will retry in background:', profileError);
        }
        setTimeout(() => {
          fetchUserData(userId, 0, true);
        }, 3000);
        return;
      }

      if (profileData) {
        // If we only got essential fields, fetch full profile now
        if (!profileData.first_name && !profileData.last_name) {
          // This shouldn't happen with our query, but just in case
          const fullProfilePromise = supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .maybeSingle();

          const { data: fullProfile, error: fullError } = await fullProfilePromise;
          if (!fullError && fullProfile) {
            profileData = fullProfile;
          }
        }

        // console.log('Profile loaded successfully:', profileData);
        setProfile(profileData);

        // Fetch tenant separately if tenant_id exists (with timeout)
        if (profileData.tenant_id) {
          try {
            const tenantPromise = supabase
              .from('tenants')
              .select('*')
              .eq('id', profileData.tenant_id)
              .maybeSingle();

            const tenantTimeout = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Tenant fetch timeout')), 10000)
            );

            const { data: tenantData } = await Promise.race([
              tenantPromise,
              tenantTimeout
            ]);

            if (tenantData) {
              setTenant(tenantData);
            }
          } catch (tenantErr) {
            console.warn('Tenant fetch failed (non-critical):', tenantErr);
            // Don't block on tenant fetch failure - can retry later if needed
          }
        }

        setError(null);
      } else {
        // No profile found - schedule background retry
        if (import.meta.env.DEV) console.log('No profile found yet - will retry in background');
        setTimeout(() => {
          fetchUserData(userId, 0, true);
        }, 3000);
      }
    } catch (err) {
      // Retry on unexpected errors - quick retry or background
      if (retryCount < maxRetries && !isBackgroundRetry) {
        if (import.meta.env.DEV) console.log(`Unexpected error in fetchUserData, quick retry (${retryCount + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchUserData(userId, retryCount + 1, false);
      }
      // Max retries exceeded - schedule background retry
      if (!isBackgroundRetry) {
        console.warn('Error in fetchUserData after retries, will retry in background:', err.message);
      }
      setTimeout(() => {
        fetchUserData(userId, 0, true);
      }, 3000);
    }
  };

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      try {
        // Get current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (session?.user && mounted) {
          setUser(session.user);
          
          // Set Sentry user context (non-blocking)
          import('../config/sentry')
            .then(({ setSentryUserContext }) => {
              setSentryUserContext(session.user);
            })
            .catch(() => {
              // Sentry not configured, ignore
            });
          
          // Don't await - let the app load while profile fetches in background
          fetchUserData(session.user.id).catch(err => {
            console.warn('Initial profile fetch failed, will retry:', err);
          });
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError(err.message);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Track if we're currently fetching to prevent duplicate fetches
    let isFetchingProfile = false;
    let lastFetchUserId = null;
    let fetchTimeout = null;

    // Listen to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // console.log('Auth state changed:', event);

        if (mounted) {
          if (session?.user) {
            setUser(session.user);
            setLoading(false); // Set loading false immediately to prevent loop

            // Only fetch if:
            // 1. Not already fetching
            // 2. User ID changed (new user logged in)
            // 3. Not INITIAL_SESSION (to prevent duplicate fetches on page load)
            const shouldFetch = !isFetchingProfile &&
              (lastFetchUserId !== session.user.id || event === 'SIGNED_IN') &&
              event !== 'INITIAL_SESSION';

            if (shouldFetch) {
              // Clear any pending fetch timeout
              if (fetchTimeout) {
                clearTimeout(fetchTimeout);
                fetchTimeout = null;
              }

              isFetchingProfile = true;
              lastFetchUserId = session.user.id;

              // Don't await - fetch in background to not block UI
              fetchUserData(session.user.id)
                .catch(err => {
                  console.error('Error fetching user data:', err);
                })
                .finally(() => {
                  isFetchingProfile = false;
                });
            } else if (event === 'INITIAL_SESSION' && session?.user && !profile) {
              // INITIAL_SESSION with no profile - fetch after a short delay
              // This handles the case where INITIAL_SESSION fires before SIGNED_IN
              fetchTimeout = setTimeout(() => {
                if (!isFetchingProfile && lastFetchUserId === session.user.id) {
                  isFetchingProfile = true;
                  fetchUserData(session.user.id).finally(() => {
                    isFetchingProfile = false;
                  });
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
              }, 500); // fetchUserData and profile are intentionally not in deps - only check once
            }
          } else {
            // Only clear session if it's a SIGNED_OUT event, not TOKEN_REFRESHED or other events
            // This prevents clearing session on temporary connection issues
            if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
              // Before clearing, verify session is actually invalid
              // Supabase may trigger SIGNED_OUT on refresh failures (500 errors)
              // but the session might still be valid
              const { data: { session: verifySession } } = await supabase.auth.getSession();

              if (!verifySession) {
                // Session is actually gone - clear state
                if (import.meta.env.DEV) console.log('Session confirmed invalid, clearing state');
                setUser(null);
                setProfile(null);
                setTenant(null);
                setError(null);
                setLoading(false);
              } else {
                // Session still exists - refresh failure was temporary
                // Don't clear state, just log the issue
                console.warn('SIGNED_OUT event received but session still valid - likely refresh failure');
                // Keep user logged in with existing session
                setUser(verifySession.user);
              }
            } else if (event === 'TOKEN_REFRESHED') {
              // Token was refreshed, keep user logged in
              // The session will be updated by the refresh handler
              if (import.meta.env.DEV) console.log('Token refreshed, maintaining session');
              consecutiveRefreshFailuresRef.current = 0; // Reset failure count on successful refresh
            } else if (event === 'INITIAL_SESSION') {
              // INITIAL_SESSION with no session is normal on first load
              // Don't clear anything, just wait for actual session
              if (import.meta.env.DEV) console.log('Initial session check - waiting for session');
            } else {
              // For other events, try to get session again
              // Don't immediately clear - might be temporary
              const { data: { session: currentSession } } = await supabase.auth.getSession();
              if (!currentSession) {
                // Only clear if we're sure there's no session
                if (import.meta.env.DEV) console.log('No session found, clearing user state');
                setUser(null);
                setProfile(null);
                setTenant(null);
                setError(null);
                setLoading(false);
              }
            }
          }
        }
      }
    );

    // Optimized session refresh to prevent automatic sign-out
    // Check every 1 minute and refresh if token expires within 5 minutes
    // Reduced frequency to avoid rate limiting and 500 errors
    // Includes exponential backoff for failed refresh attempts
    const refreshInterval = setInterval(async () => {
      if (mounted) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            // Check if token is close to expiring (within 15 minutes)
            const expiresAt = session.expires_at;
            const now = Math.floor(Date.now() / 1000);
            const timeUntilExpiry = expiresAt - now;

            // Refresh if token expires within 5 minutes (300 seconds)
            // Reduced from 15 minutes to avoid too many refresh attempts and rate limiting
            if (timeUntilExpiry < 300) {
              if (import.meta.env.DEV) console.log(`[Auth] Token expires in ${timeUntilExpiry}s, refreshing...`);

              // Use retry logic with exponential backoff for refresh
              const { data, error } = await refreshSessionWithRetry(session, 3);

              if (error) {
                // Check if it's a transient error (500, timeout, network)
                if (isTransientError(error)) {
                  console.warn('[Auth] Transient refresh error, will retry next interval');
                  consecutiveRefreshFailuresRef.current += 1;
                  // Only sign out if we've had too many consecutive failures
                  if (consecutiveRefreshFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                    console.error('[Auth] Too many consecutive refresh failures, signing out');
                    // Don't sign out immediately - let the user continue with current session
                    // The session might still be valid for a few more minutes
                  }

                  // Don't sign out on transient errors - might be temporary
                  // Check if current session is still valid
                  const { data: { session: currentSession } } = await supabase.auth.getSession();
                  if (currentSession && mounted) {
                    if (import.meta.env.DEV) console.log('[Auth] Refresh failed but session still valid');
                    // Keep using existing session
                  }
                } else {
                  // Non-transient error (e.g., invalid token) - sign out immediately
                  console.error('[Auth] Non-transient refresh error, signing out:', error);
                  if (mounted) {
                    await supabase.auth.signOut();
                  }
                }
              } else if (data?.session) {
                if (import.meta.env.DEV) console.log('[Auth] Token refreshed successfully');
                consecutiveRefreshFailuresRef.current = 0; // Reset failure count on success

                if (mounted) {
                  setUser(data.session.user);
                }
              }
            }
          } else {
            // No session - try to get it again (might be temporary)
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (retrySession && mounted) {
              if (import.meta.env.DEV) console.log('Session recovered');
              setUser(retrySession.user);
            }
          }
        } catch (err) {
          console.warn('Error refreshing session:', err);
          // Don't sign out on refresh error - might be temporary network issue
        }
      }
    }, 60000); // Check every 1 minute (reduced from 2 minutes to be more responsive)

    // Also listen for TOKEN_REFRESHED events from Supabase
    const { data: { subscription: refreshSubscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'TOKEN_REFRESHED' && session && mounted) {
          setUser(session.user);
          consecutiveRefreshFailuresRef.current = 0; // Reset failure count on successful refresh
        }
      }
    );

    return () => {
      mounted = false;
      subscription?.unsubscribe();
      refreshSubscription?.unsubscribe();
      clearInterval(refreshInterval);
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run once on mount. fetchUserData and profile are stable.

  // Sign out method
  const signOut = async () => {
    try {
      // Clear Sentry user context (non-blocking)
      import('../config/sentry')
        .then(({ clearSentryUserContext }) => {
          clearSentryUserContext();
        })
        .catch(() => {
          // Sentry not configured, ignore
        });
      
      // Clear state immediately to ensure UI updates
      setUser(null);
      setProfile(null);
      setTenant(null);
      setError(null);
      setLoading(false);

      // Sign out from Supabase
      const { error } = await supabaseAuthService.signOut();
      if (error) {
        // Even if signout fails, we've cleared local state
        console.error('Error signing out from Supabase:', error);
        // Don't throw - state is already cleared
      }

      // The onAuthStateChange listener will also handle clearing state
      // but we clear it immediately here to ensure UI updates right away
    } catch (err) {
      console.error('Error signing out:', err);
      // Clear state even on error
      setUser(null);
      setProfile(null);
      setTenant(null);
      setError(null);
      setLoading(false);
      // Don't throw - state is cleared, let navigation happen
    }
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      await fetchUserData(user.id);
    }
  };

  // Update profile
  const updateProfile = async (updates) => {
    try {
      if (!user) throw new Error('No user logged in');

      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(data);
      return { data, error: null };
    } catch (err) {
      console.error('Error updating profile:', err);
      return { data: null, error: err };
    }
  };

  // Compute isApproved: patients, staff, admins are immediately approved
  // Physicians and sponsors require approval_status === 'approved'
  const isApproved = profile ? (
    ['patient', 'clinic_staff', 'clinic_admin'].includes(profile.tenant_role) ||
    profile.approval_status === 'approved'
  ) : false;

  const value = {
    user,
    profile,
    tenant,
    loading,
    error,
    signOut,
    logout: signOut, // Alias for signOut
    refreshProfile,
    updateProfile,
    isAuthenticated: !!user,
    // Super admins have platform_role OR tenant_role = 'super_admin'
    // Clinic admins have tenant_role = 'clinic_admin'
    isSuperAdmin: profile?.platform_role === 'super_admin' || profile?.tenant_role === 'super_admin',
    isClinicAdmin: profile?.tenant_role === 'clinic_admin',
    isAdmin: profile?.platform_role === 'super_admin' ||
      profile?.tenant_role === 'super_admin' ||
      profile?.tenant_role === 'clinic_admin',
    isProvider: profile?.tenant_role === 'physician' ||
      profile?.tenant_role === 'clinic_staff' ||
      profile?.platform_role === 'global_physician',
    isPatient: profile?.tenant_role === 'patient',
    isSponsor: profile?.tenant_role === 'sponsor',
    isApproved, // Whether user is approved (for physicians/sponsors) or immediately approved (others)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
